import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface MobileKeyboardNavContextProps {
  mobileLevelOneMenuItemIdToFocusNext: string | null;
  setMobileLevelOneMenuItemIdToFocusNext: React.Dispatch<React.SetStateAction<string | null>>;
  mobileLevelTwoMenuItemIdToFocusNext: string | null;
  setMobileLevelTwoMenuItemIdToFocusNext: React.Dispatch<React.SetStateAction<string | null>>;
  mobileFocusFavoritesMenuItemNext: boolean;
  setMobileFocusFavoritesMenuItemNext: React.Dispatch<React.SetStateAction<boolean>>;
  mobileFocusSwitchMenuItemNext: boolean;
  setMobileFocusSwitchMenuItemNext: React.Dispatch<React.SetStateAction<boolean>>;
  firstMobileMenuItemRef: React.MutableRefObject<HTMLLIElement>;
  firstMobileMenuItemIsContextSwitcher: boolean;
  setFirstMobileMenuItemIsContextSwitcher: React.Dispatch<React.SetStateAction<boolean>>;
  clearFocusNextStates: () => void;
}

export const MobileKeyboardNavContext = createContext<MobileKeyboardNavContextProps | null>(null);

export const useMobileKeyboardNavContext = () => useContext(MobileKeyboardNavContext);

interface MobileKeyboardNavProviderProps {
  children: React.ReactNode;
}

export const MobileKeyboardNavProvider: React.FC<MobileKeyboardNavProviderProps> = ({ children }) => {
  // Used for moving focus to correct item after hitting back button on mobile with keyboard navigation
  const [mobileLevelOneMenuItemIdToFocusNext, setMobileLevelOneMenuItemIdToFocusNext] = useState<string | null>(null);
  const [mobileLevelTwoMenuItemIdToFocusNext, setMobileLevelTwoMenuItemIdToFocusNext] = useState<string | null>(null);
  const [mobileFocusFavoritesMenuItemNext, setMobileFocusFavoritesMenuItemNext] = useState<boolean>(false);
  const [mobileFocusSwitchMenuItemNext, setMobileFocusSwitchMenuItemNext] = useState<boolean>(false);

  const firstMobileMenuItemRef = React.useRef<HTMLLIElement>(null);
  const [firstMobileMenuItemIsContextSwitcher, setFirstMobileMenuItemIsContextSwitcher] = useState<boolean>(false);

  const clearFocusNextStates = () => {
    setMobileLevelOneMenuItemIdToFocusNext(null);
    setMobileLevelTwoMenuItemIdToFocusNext(null);
    setMobileFocusFavoritesMenuItemNext(false);
    setMobileFocusSwitchMenuItemNext(false);
  };

  // These 3 hooks ensure that only one of these variables can be truthy at a time, so that focus is set correctly.
  // For example, when the back button is pressed from the favorites submenu, the focus should not move back to
  // the previously opened menu item, but to the favorites menu item.
  useEffect(() => {
    if (mobileLevelOneMenuItemIdToFocusNext) {
      setMobileFocusFavoritesMenuItemNext(false);
      setMobileFocusSwitchMenuItemNext(false);
    }
  }, [mobileLevelOneMenuItemIdToFocusNext]);

  useEffect(() => {
    if (mobileFocusFavoritesMenuItemNext) {
      setMobileLevelOneMenuItemIdToFocusNext(null);
      setMobileFocusSwitchMenuItemNext(false);
    }
  }, [mobileFocusFavoritesMenuItemNext]);

  useEffect(() => {
    if (mobileFocusSwitchMenuItemNext) {
      setMobileLevelOneMenuItemIdToFocusNext(null);
      setMobileFocusFavoritesMenuItemNext(false);
    }
  }, [mobileFocusSwitchMenuItemNext]);

  const value = useMemo(
    () => ({
      mobileLevelOneMenuItemIdToFocusNext,
      setMobileLevelOneMenuItemIdToFocusNext,
      mobileLevelTwoMenuItemIdToFocusNext,
      setMobileLevelTwoMenuItemIdToFocusNext,
      mobileFocusFavoritesMenuItemNext,
      setMobileFocusFavoritesMenuItemNext,
      mobileFocusSwitchMenuItemNext,
      setMobileFocusSwitchMenuItemNext,
      firstMobileMenuItemRef,
      firstMobileMenuItemIsContextSwitcher,
      setFirstMobileMenuItemIsContextSwitcher,
      clearFocusNextStates
    }),
    [
      mobileLevelOneMenuItemIdToFocusNext,
      mobileLevelTwoMenuItemIdToFocusNext,
      mobileFocusFavoritesMenuItemNext,
      mobileFocusSwitchMenuItemNext,
      firstMobileMenuItemRef,
      firstMobileMenuItemIsContextSwitcher
    ]
  );

  return <MobileKeyboardNavContext.Provider value={value}>{children}</MobileKeyboardNavContext.Provider>;
};
